import { Link } from "react-router-dom";
import PageHeader from "@/components/feature/PageHeader";
import { useSEO } from "@/hooks/useSEO";

const milestones = [
  { year: "2016", title: "朝桐光成立", desc: "由一群热爱电影的青年创作者发起，从一部短片起步，开启内容创作之路。" },
  { year: "2018", title: "首部院线电影上映", desc: "首部独立出品的剧情长片登陆院线，获得业内与观众的双重认可。" },
  { year: "2021", title: "拓展剧集业务", desc: "成立剧集制作中心，年代剧《城南旧巷》开播即收获良好口碑。" },
  { year: "2023", title: "多元题材并进", desc: "悬疑、科幻、历史多线发力，多部作品登陆主流平台并斩获奖项。" },
  { year: "2026", title: "面向未来", desc: "发布年度八大片单，启动青年创作者扶持计划，持续孵化优质内容。" },
];

const team = [
  { role: "出品人 / 总制片", name: "林见山", desc: "负责品牌内容战略与项目统筹，深耕影视行业十五年。" },
  { role: "内容总监", name: "周慕白", desc: "主导剧本开发与创作方向，代表作《长夜将明》《暗涌》。" },
  { role: "制作总监", name: "柳砚青", desc: "把控制作全流程，擅长东方美学与实景拍摄。" },
  { role: "发行负责人", name: "秦子墨", desc: "负责国内外发行与版权合作，推动作品走向更广市场。" },
];

export default function About() {
  useSEO({
    title: "关于我们 - 朝桐光影视品牌",
    description:
      "了解朝桐光品牌的发展历程、创作团队与网站使命。朝桐光专注影视内容创作与出品，致力于用光影讲好每一个故事。",
    keywords: "朝桐光关于我们,朝桐光品牌,影视制作团队,朝桐光使命",
  });

  return (
    <div>
      <PageHeader
        title="关于朝桐光"
        desc="朝桐光是一家专注影视内容创作与出品的品牌。我们相信，好的作品经得起时间，也值得被认真对待。"
        breadcrumb={[{ label: "首页", to: "/" }, { label: "关于我们" }]}
      />

      <div className="mx-auto w-full max-w-page px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <div className="mb-3 flex items-center gap-2">
              <span className="h-1 w-6 rounded-full bg-accent-500" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">Our Story</span>
            </div>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">品牌历史与发展</h2>
            <p className="mt-4 text-sm leading-8 text-foreground-700 md:text-base">
              自 2016 年成立以来，朝桐光从一部短片出发，逐步成长为覆盖电影、电视剧策划、制作与发行的综合影视品牌。我们始终把内容放在第一位，坚持长期主义，不追逐一时的热度，而是认真打磨每一个故事。
            </p>

            <ol className="mt-8 relative border-l border-background-300 pl-6 md:pl-8">
              {milestones.map((m) => (
                <li key={m.year} className="relative mb-8 last:mb-0">
                  <span className="absolute -left-[31px] top-1.5 flex h-3 w-3 items-center justify-center rounded-full bg-primary-500 ring-4 ring-background-50 md:-left-[39px]" />
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-heading text-lg font-black text-accent-600">{m.year}</span>
                    <h3 className="text-base font-bold text-foreground-950">{m.title}</h3>
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{m.desc}</p>
                </li>
              ))}
            </ol>
          </div>

          <aside className="flex flex-col gap-6">
            <div className="rounded-lg border border-background-200 bg-background-100/60 p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                  <i className="ri-flag-line"></i>
                </span>
                网站使命
              </h3>
              <p className="mt-3 text-sm leading-7 text-foreground-600">
                我们希望把朝桐光的每一部作品、每一位创作者与每一条动态，都真诚地呈现给影迷，让了解朝桐光这件事变得简单而愉快。
              </p>
            </div>
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h3 className="flex items-center gap-2 text-base font-bold text-foreground-950">
                <span className="flex h-4 w-4 items-center justify-center text-primary-600">
                  <i className="ri-contacts-line"></i>
                </span>
                联系方式
              </h3>
              <ul className="mt-3 space-y-2.5 text-sm text-foreground-600">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line text-foreground-400"></i>
                  contact@zhaotongguang.example
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-phone-line text-foreground-400"></i>
                  400-000-0000（工作日 9:00-18:00）
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-map-pin-line text-foreground-400"></i>
                  北京市朝阳区影视产业园 A 座 12 层
                </li>
              </ul>
              <Link
                to="/contact"
                className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-send-plane-line"></i>
                前往联系页面
              </Link>
            </div>
          </aside>
        </div>

        <div className="mt-14">
          <div className="mb-3 flex items-center gap-2">
            <span className="h-1 w-6 rounded-full bg-accent-500" />
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">Our Team</span>
          </div>
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">团队简介</h2>
          <p className="mt-2 max-w-2xl text-sm text-foreground-600">
            一支经验丰富又保有热情的创作团队，是朝桐光持续输出好内容的底气。
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {team.map((member) => (
              <div key={member.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-black text-primary-700">
                  {member.name.slice(0, 1)}
                </div>
                <h3 className="mt-4 text-base font-bold text-foreground-950">{member.name}</h3>
                <p className="mt-0.5 text-xs font-medium text-accent-600">{member.role}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{member.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-6 rounded-lg bg-primary-950 p-6 md:grid-cols-3 md:p-10">
          {[
            { value: "10 年", label: "内容创作经验" },
            { value: "12+", label: "累计出品作品" },
            { value: "8 项", label: "行业奖项及提名" },
          ].map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-heading text-3xl font-black text-accent-400 md:text-4xl">{s.value}</div>
              <div className="mt-2 text-sm text-background-300">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}